import React, { useEffect, useState, useCallback, useMemo } from "react";
import { getOrders } from "../../services/woocommerce";
import Layout from "../../components/Layout";
import { ShoppingCart, Package, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DynamicTable from "../../components/Table/DataTable";
import { IconEye } from "../../components/icons/icons";
import TablePaginationActions from "../../components/Table/tablePagination";
import LoadingScreen from "../../components/LoadingScreen";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const statusColors: Record<string, string> = {
    pending: "bg-yellow-200 text-yellow-900",
    processing: "bg-sky-200 text-sky-900",
    completed: "bg-emerald-200 text-emerald-900",
    cancelled: "bg-rose-200 text-rose-900",
    refunded: "bg-violet-200 text-violet-900",
    failed: "bg-stone-200 text-stone-900",
};

// Enhanced PDF Invoice Generator
const generateEnhancedInvoice = (order: any) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = 20;

    // Modern gradient header background
    doc.setFillColor(79, 70, 229); // Indigo-600
    doc.rect(0, 0, pageWidth, 55, 'F');
    
    // Add subtle pattern overlay (optional decorative lines)
    doc.setDrawColor(99, 102, 241); // Indigo-400
    doc.setLineWidth(0.5);
    for (let i = 0; i < 5; i++) {
        doc.line(0, 10 + (i * 10), pageWidth, 10 + (i * 10));
    }

    // Company name with modern styling
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text('PETSYNC360', margin, 25);
    
    // Company tagline
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text('Premium Pet Care Solutions', margin, 33);
    
    // Contact info in header
    doc.setFontSize(8);
    doc.text('Chennai, Tamil Nadu | support@petsync360.com | +91 98765 43210', margin, 45);

    // Invoice badge on right side
    doc.setFillColor(255, 255, 255);
    const badgeX = pageWidth - margin - 45;
    doc.roundedRect(badgeX, 15, 45, 25, 3, 3, 'F');
    doc.setTextColor(79, 70, 229);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text('INVOICE', badgeX + 22.5, 23, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`#${order.id}`, badgeX + 22.5, 32, { align: 'center' });

    yPos = 70;

    // Order information cards
    doc.setFillColor(249, 250, 251); // Gray-50
    doc.roundedRect(margin, yPos, (pageWidth - (margin * 2)) / 2 - 3, 35, 2, 2, 'F');
    
    doc.setTextColor(79, 70, 229);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('ORDER DETAILS', margin + 5, yPos + 8);
    
    doc.setTextColor(55, 65, 81); // Gray-700
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Order Number: #${order.id}`, margin + 5, yPos + 16);
    doc.text(`Order Date: ${new Date(order.date_created).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    })}`, margin + 5, yPos + 23);
    doc.text(`Payment: ${order.payment_method_title || 'N/A'}`, margin + 5, yPos + 30);

    // Status badge
    const statusX = pageWidth / 2 + 2;
    const status = order.status || 'pending';
    let statusColor;
    switch(status) {
        case 'completed': statusColor = [16, 185, 129]; break; // Green
        case 'processing': statusColor = [59, 130, 246]; break; // Blue
        case 'pending': statusColor = [251, 191, 36]; break; // Yellow
        case 'cancelled': statusColor = [239, 68, 68]; break; // Red
        default: statusColor = [107, 114, 128]; // Gray
    }
    
    doc.setFillColor(249, 250, 251);
    doc.roundedRect(statusX, yPos, (pageWidth - margin - statusX), 35, 2, 2, 'F');
    
    doc.setTextColor(79, 70, 229);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text('ORDER STATUS', statusX + 5, yPos + 8);
    
    doc.setFillColor(statusColor[0], statusColor[1], statusColor[2]);
    doc.roundedRect(statusX + 5, yPos + 12, 35, 8, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(status.toUpperCase(), statusX + 22.5, yPos + 17.5, { align: 'center' });

    yPos += 45;

    // Customer details section with modern card
    doc.setDrawColor(229, 231, 235); // Gray-200
    doc.setLineWidth(0.5);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin, yPos, pageWidth - (margin * 2), 40, 2, 2, 'FD');
    
    doc.setTextColor(79, 70, 229);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text('BILL TO', margin + 5, yPos + 8);
    
    const billing = order.billing || {};
    const fullName = `${billing.first_name || ''} ${billing.last_name || ''}`.trim();
    
    doc.setTextColor(17, 24, 39); // Gray-900
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(fullName || 'N/A', margin + 5, yPos + 16);
    
    doc.setTextColor(75, 85, 99); // Gray-600
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    let addressY = yPos + 22;
    if (billing.address_1) {
        doc.text(billing.address_1, margin + 5, addressY);
        addressY += 5;
    }
    if (billing.address_2) {
        doc.text(billing.address_2, margin + 5, addressY);
        addressY += 5;
    }
    const cityLine = `${billing.city || ''}, ${billing.state || ''} ${billing.postcode || ''}`.trim();
    if (cityLine.length > 2) {
        doc.text(cityLine, margin + 5, addressY);
    }
    
    // Contact info on right side
    const contactX = pageWidth / 2 + 10;
    doc.setTextColor(75, 85, 99);
    doc.setFontSize(9);
    doc.text(`Email: ${billing.email || 'N/A'}`, contactX, yPos + 16);
    doc.text(`Phone: ${billing.phone || 'N/A'}`, contactX, yPos + 22);

    yPos += 50;

    // Items table with modern styling
    const lineItems = (order.line_items || []).map((item: any, idx: number) => [
        `${idx + 1}`,
        item.name || '',
        item.quantity || 0,
        `₹${parseFloat(item.price || 0).toFixed(2)}`,
        `₹${parseFloat(item.total || 0).toFixed(2)}`
    ]);

    autoTable(doc, {
        startY: yPos,
        head: [['#', 'Item Description', 'Qty', 'Unit Price', 'Amount']],
        body: lineItems,
        theme: 'striped',
        headStyles: {
            fillColor: [79, 70, 229], // Indigo-600
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: 'bold',
            halign: 'center',
            cellPadding: 4
        },
        bodyStyles: {
            fontSize: 9,
            textColor: [55, 65, 81],
            cellPadding: 3
        },
        columnStyles: {
            0: { cellWidth: 10, halign: 'center' },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 20, halign: 'center' },
            3: { cellWidth: 30, halign: 'right' },
            4: { cellWidth: 30, halign: 'right', fontStyle: 'bold' }
        },
        alternateRowStyles: {
            fillColor: [249, 250, 251]
        },
        styles: {
            lineColor: [229, 231, 235],
            lineWidth: 0.3
        },
        margin: { left: margin, right: margin }
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;

    // Totals section with modern design
    const totalsWidth = 65;
    const totalsX = pageWidth - margin - totalsWidth;
    
    doc.setFillColor(249, 250, 251);
    doc.roundedRect(totalsX, yPos, totalsWidth, 50, 2, 2, 'F');
    
    let totalY = yPos + 8;
    const subtotal = (order.line_items || []).reduce(
        (sum: number, item: any) => sum + parseFloat(item.subtotal || item.total || 0), 
        0
    );

    doc.setTextColor(75, 85, 99);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    
    doc.text('Subtotal:', totalsX + 5, totalY);
    doc.text(`₹${subtotal.toFixed(2)}`, totalsX + totalsWidth - 5, totalY, { align: 'right' });
    totalY += 7;

    const discountTotal = parseFloat(order.discount_total || '0');
    if (discountTotal > 0) {
        doc.setTextColor(239, 68, 68); // Red for discount
        doc.text('Discount:', totalsX + 5, totalY);
        doc.text(`-₹${discountTotal.toFixed(2)}`, totalsX + totalsWidth - 5, totalY, { align: 'right' });
        totalY += 7;
    }

    const cartTax = parseFloat(order.cart_tax || '0');
    if (cartTax > 0) {
        doc.setTextColor(75, 85, 99);
        doc.text('Tax:', totalsX + 5, totalY);
        doc.text(`₹${cartTax.toFixed(2)}`, totalsX + totalsWidth - 5, totalY, { align: 'right' });
        totalY += 7;
    }

    // Separator line
    doc.setDrawColor(209, 213, 219);
    doc.setLineWidth(0.5);
    doc.line(totalsX + 5, totalY + 2, totalsX + totalsWidth - 5, totalY + 2);
    totalY += 8;

    // Grand total with emphasis
    doc.setFillColor(79, 70, 229);
    doc.roundedRect(totalsX + 3, totalY - 5, totalsWidth - 6, 12, 2, 2, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text('TOTAL', totalsX + 8, totalY + 2);
    doc.setFontSize(12);
    doc.text(`₹${parseFloat(order.total || '0').toFixed(2)}`, totalsX + totalsWidth - 8, totalY + 2, { align: 'right' });

    // Footer with modern styling
    const footerY = pageHeight - 25;
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY, pageWidth - margin, footerY);
    
    doc.setTextColor(107, 114, 128);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text('Thank you for your business!', pageWidth / 2, footerY + 6, { align: 'center' });
    
    doc.setFontSize(7);
    doc.setFont("helvetica", "italic");
    doc.text('This is a computer-generated invoice. No signature required.', pageWidth / 2, footerY + 11, { align: 'center' });
    
    doc.setFontSize(7);
    doc.text('Terms & Conditions apply | For queries: support@petsync360.com', pageWidth / 2, footerY + 16, { align: 'center' });

    doc.save(`Invoice-${order.id}-Petsync360.pdf`);
};

const OrderListPage = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);
    const [statusCount, setStatusCount] = useState<Record<string, number>>({});
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const fetchOrders = useCallback(async (pageNumber = 1) => {
        setLoading(true);
        try {
            const params: any = {
                per_page: rowsPerPage,
                page: pageNumber,
            };

            if (searchTerm.trim()) params.search = searchTerm.trim();
            if (statusFilter) params.status = statusFilter;

            const res = await getOrders(params) as any;
            setOrders(res.data);
            setTotalCount(parseInt(res.headers["x-wp-total"]) || 0);
            setError("");
        } catch (e) {
            setError("Failed to load orders.");
        } finally {
            setLoading(false);
        }
    }, [rowsPerPage, searchTerm, statusFilter]);

    useEffect(() => {
        fetchOrders(page);
    }, [page, fetchOrders]);

    const fetchAllOrdersStatusCount = useCallback(async () => {
        try {
            let currentPage = 1;
            let totalPages = 1;
            const counts: Record<string, number> = {};

            while (currentPage <= totalPages) {
                const res = await getOrders({ per_page: 100, page: currentPage }) as any;
                res.data.forEach((order: any) => {
                    counts[order.status] = (counts[order.status] || 0) + 1;
                });

                if (currentPage === 1) {
                    totalPages = Math.ceil((parseInt(res.headers["x-wp-total"]) || 0) / 100);
                }
                currentPage++;
            }

            setStatusCount(counts);
        } catch (e) {
            console.error("Failed to load order statuses:", e);
        }
    }, []);

    useEffect(() => {
        fetchAllOrdersStatusCount();
    }, [fetchAllOrdersStatusCount]);

    const columns = useMemo(() => [
        {
            key: 'serial',
            label: 'S.No',
            align: 'center',
            render: (_row: any, index: number) => (page - 1) * rowsPerPage + index + 1,
        },
        {
            key: 'orderid',
            label: 'Order Id',
            align: 'center',
            render: (row: any) => (
                <div className="text-sm text-indigo-600 font-semibold">
                    #{row?.id ?? '-'}
                </div>
            ),
        },
        {
            key: 'Customer Details',
            label: 'Customer Details',
            align: 'center',
            render: (row: any) => {
                const fullName = `${row?.billing?.first_name || ''} ${row?.billing?.last_name || ''}`.trim();
                return (
                    <div className="py-2">
                        <div className="font-semibold text-gray-800 text-base">
                            {fullName || '-'}
                        </div>
                        <div className="text-sm text-gray-600 mt-0.5">{row?.billing?.email || '-'}</div>
                        <div className="text-sm text-gray-500 mt-0.5">{row?.billing?.phone || '-'}</div>
                    </div>
                );
            }
        },
        {
            key: 'products',
            label: 'Products',
            align: 'left',
            render: (row: any) => {
                const lineItems = row?.line_items || [];
                if (lineItems.length === 0) return <span className="text-gray-400">-</span>;

                return (
                    <div className="py-2 space-y-2">
                        {lineItems.map((item: any, idx: number) => (
                            <div
                                key={idx}
                                className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-3 border border-indigo-100"
                            >
                                <div className="flex items-start gap-2">
                                    <Package size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 text-sm leading-tight mb-1">
                                            {item.name}
                                        </div>
                                        <div className="flex items-center gap-3 text-xs">
                                            <span className="bg-white px-2 py-1 rounded-md font-semibold text-indigo-700 border border-indigo-200">
                                                Qty: {item.quantity}
                                            </span>
                                            <span className="text-gray-600">
                                                ₹{parseFloat(item.price).toFixed(2)}
                                            </span>
                                            <span className="font-bold text-emerald-700">
                                                ₹{parseFloat(item.total).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                );
            }
        },
        {
            key: 'totalamount',
            label: 'Total',
            align: 'center',
            render: (row: any) => (
                <div className="font-bold text-lg text-emerald-700">
                    ₹{parseFloat(row.total).toFixed(2)}
                </div>
            ),
        },
        {
            key: 'date',
            label: 'Order Date & Time',
            align: 'center',
            render: (row: any) => {
                const date = new Date(row.date_created);
                const formattedDate = date.toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
                const formattedTime = date.toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });

                return (
                    <div className="text-sm">
                        <div className="font-semibold text-gray-800">{formattedDate}</div>
                        <div className="text-gray-600 text-xs mt-0.5">{formattedTime}</div>
                    </div>
                );
            }
        },
        {
            key: 'status',
            label: 'Status',
            align: 'center',
            render: (row: any) => {
                const status = row.status || "unknown";
                const className = statusColors[status] || "bg-gray-200 text-gray-700";
                return (
                    <span className={`px-3 py-1.5 rounded-full font-bold text-xs uppercase tracking-wide ${className} shadow-sm`}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                );
            }
        },
    ], [page, rowsPerPage]);

    const handleShowDetails = useCallback((row: any) => {
        navigate(`/orders/${row.id}`);
    }, [navigate]);

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage + 1);
    }, []);

    const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(1);
    }, []);

    const handleClearFilters = useCallback(() => {
        setSearchInput("");
        setSearchTerm("");
        setStatusFilter("");
        setPage(1);
    }, []);

    const handleSearch = useCallback(() => {
        setSearchTerm(searchInput);
        setPage(1);
    }, [searchInput]);

    const actions = useMemo(() => [
        {
            label: 'View',
            color: '#2196f3',
            icon: <IconEye size={20} />,
            onClick: handleShowDetails
        },
        {
            label: 'Download Invoice',
            color: '#4CAF50',
            icon: <Download size={20} />,
            onClick: generateEnhancedInvoice
        },
    ], [handleShowDetails]);

    if (loading && orders.length === 0) {
        return <LoadingScreen message="Loading Orders..." />;
    }

    if (error && orders.length === 0) {
        return (
            <Layout>
                <div className="text-center mt-10 text-red-500 font-semibold">{error}</div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="mx-auto p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
                {/* Header Section */}
                <div className="p-6 mb-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <h2 className="text-4xl font-bold flex items-center text-gray-900">
                            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-3 rounded-xl mr-4 shadow-lg">
                                <ShoppingCart size={32} className="text-white" />
                            </div>
                            Orders
                            <span className="ml-3 text-2xl font-semibold text-indigo-600">
                                ({totalCount})
                            </span>
                        </h2>
                    </div>

                    {/* Status Count Badges */}
                    <div className="flex gap-3 mt-6 flex-wrap">
                        {Object.entries(statusCount).map(([status, count]) => (
                            <div
                                key={status}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm shadow-md transition-transform hover:scale-105 cursor-pointer ${
                                    statusColors[status] || "bg-gray-200 text-gray-700"
                                }`}
                                onClick={() => {
                                    setStatusFilter(status);
                                    setPage(1);
                                }}
                            >
                                <span className="uppercase tracking-wide">
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </span>
                                <span className="bg-white bg-opacity-60 px-2 py-0.5 rounded-full font-extrabold">
                                    {count}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Filters Section */}
                <div className="p-5 mb-6">
                    <div className="flex flex-wrap gap-4 items-center">
                        <div className="flex gap-2 flex-1 min-w-[300px]">
                            <input
                                type="text"
                                placeholder="Search by customer name, email, order ID..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                className="flex-1 border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                            />
                            <button
                                onClick={handleSearch}
                                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
                            >
                                Search
                            </button>
                            {(searchInput || searchTerm || statusFilter) && (
                                <button
                                    onClick={handleClearFilters}
                                    className="bg-gray-300 text-gray-700 px-5 py-2.5 rounded-lg font-semibold hover:bg-gray-400 transition-all shadow-md"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        <select
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            className="border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 font-medium bg-white shadow-sm transition-all"
                        >
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="processing">Processing</option>
                            <option value="on-hold">On Hold</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="refunded">Refunded</option>
                            <option value="failed">Failed</option>
                            {Object.keys(statusCount)
                                .filter(status => 
                                    !['pending', 'processing', 'on-hold', 'completed', 'cancelled', 'refunded', 'failed'].includes(status)
                                )
                                .map(status => (
                                    <option key={status} value={status}>
                                        {status.charAt(0).toUpperCase() + status.slice(1)}
                                    </option>
                                ))}
                        </select>
                    </div>
                </div>

                {/* Table Section */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
                    {orders.length === 0 ? (
                        <div className="text-center py-16">
                            <div className="bg-gray-100 rounded-full w-24 h-24 mx-auto flex items-center justify-center mb-4">
                                <ShoppingCart size={48} className="text-gray-400" />
                            </div>
                            <p className="text-gray-500 text-lg font-medium">No orders found.</p>
                        </div>
                    ) : (
                        <DynamicTable
                            columns={columns}
                            data={orders}
                            loading={loading}
                            actions={actions}
                            page={page - 1}
                            error={error}
                            rowsPerPage={rowsPerPage}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            ActionsComponent={TablePaginationActions}
                        />
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default OrderListPage;